/**
 * FLOW BUILDER SERVICE
 * Frontend flow builder funkciók - mentés, betöltés, listázás
 */

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit as firestoreLimit,
  Timestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../lib/firebase';
import { FlowTemplate, FlowNode, FlowEdge } from '../types/flowBuilder';
import { FLOW_ENGINE_ENDPOINTS } from '../config/flowEngine';
import { getFlowExecutionUrl } from '../config/flowEngine';
// TODO: Migrálás a közös sémára
// import { FlowTemplate, FlowNode, FlowEdge, normalizeFlowData } from '../shared-schema/flow-types';

/**
 * Get Firebase ID token for authentication
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

export interface FlowExecutionRequest {
  flowId: string;
  organizationId: string;
  initialData?: any;
  userId?: string;
}

export interface FlowExecutionResult {
  success: boolean;
  executionId: string;
  result: any;
  message: string;
}

/**
 * Save flow template to Firestore
 */
export async function saveFlow(
  flowData: Partial<FlowTemplate>, 
  organizationId: string, 
  userId: string
): Promise<{ success: boolean; flowId: string; message: string }> {
  try {
    const flowDoc = {
      ...flowData,
      organizationId,
      createdBy: userId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      version: 1,
      isActive: true
    };

    const docRef = await addDoc(collection(db, 'flows'), flowDoc);
    
    console.log('Flow saved successfully:', { flowId: docRef.id, organizationId });
    
    return {
      success: true,
      flowId: docRef.id,
      message: 'Flow saved successfully'
    };
  } catch (error) {
    console.error('Error saving flow:', error);
    throw new Error(`Failed to save flow: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update existing flow
 */
export async function updateFlow(
  flowId: string,
  flowData: Partial<FlowTemplate>,
  organizationId: string,
  userId: string
): Promise<{ success: boolean; message: string }> {
  try {
    const flowRef = doc(db, 'flows', flowId);
    const flowDoc = await getDoc(flowRef);
    
    if (!flowDoc.exists()) {
      throw new Error('Flow not found');
    }
    
    const flow = flowDoc.data();
    if (flow.organizationId !== organizationId) {
      throw new Error('Access denied to flow');
    }
    
    await updateDoc(flowRef, {
      ...flowData,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
      version: (flow.version || 1) + 1
    });
    
    // Backend API hívás a scheduler frissítéséhez
    try {
      const token = await getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${FLOW_ENGINE_ENDPOINTS.flows}/${flowId}?organizationId=${organizationId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(flowData),
        }
      );
      
      if (!response.ok) {
        console.warn('Failed to update flow in backend, but Firestore updated successfully');
      }
    } catch (backendError) {
      console.warn('Backend update failed, but Firestore updated:', backendError);
    }
    
    return {
      success: true,
      message: 'Flow updated successfully'
    };
  } catch (error) {
    console.error('Error updating flow:', error);
    throw new Error(`Failed to update flow: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get flow by ID
 */
export async function getFlow(
  flowId: string, 
  organizationId: string
): Promise<FlowTemplate> {
  try {
    const flowDoc = await getDoc(doc(db, 'flows', flowId));
    
    if (!flowDoc.exists()) {
      throw new Error('Flow not found');
    }
    
    const flow = flowDoc.data();
    
    if (flow.organizationId !== organizationId) {
      throw new Error('Access denied to flow');
    }
    
    return {
      id: flowDoc.id,
      ...flow,
      createdAt: flow.createdAt?.toDate(),
      updatedAt: flow.updatedAt?.toDate()
    } as FlowTemplate;
  } catch (error) {
    console.error('Error getting flow:', error);
    throw new Error(`Failed to get flow: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * List flows for organization
 */
export async function listFlows(
  organizationId: string, 
  limit: number = 50
): Promise<FlowTemplate[]> {
  try {
    const flowsQuery = query(
      collection(db, 'flows'),
      where('organizationId', '==', organizationId),
      firestoreLimit(limit)
    );
    
    const querySnapshot = await getDocs(flowsQuery);
    
    const flows = querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as FlowTemplate;
      })
      .filter(flow => flow.isActive !== false) // Csak aktív flow-k
      .sort((a, b) => (b.updatedAt?.getTime() || 0) - (a.updatedAt?.getTime() || 0)); // Rendezés dátum szerint
    
    return flows;
  } catch (error) {
    console.error('Error listing flows:', error);
    throw new Error(`Failed to list flows: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Delete flow
 */
export async function deleteFlow(
  flowId: string, 
  organizationId: string
): Promise<{ success: boolean; message: string }> {
  try {
    const flowRef = doc(db, 'flows', flowId);
    const flowDoc = await getDoc(flowRef);
    
    if (!flowDoc.exists()) {
      throw new Error('Flow not found');
    }
    
    const flow = flowDoc.data();
    if (flow.organizationId !== organizationId) {
      throw new Error('Access denied to flow');
    }
    
    // Soft delete - mark as inactive
    await updateDoc(flowRef, {
      isActive: false,
      deletedAt: Timestamp.now()
    });
    
    return {
      success: true,
      message: 'Flow deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting flow:', error);
    throw new Error(`Failed to delete flow: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Execute flow (calls Python backend)
 */
export async function executeFlow(
  request: FlowExecutionRequest
): Promise<FlowExecutionResult> {
  try {
    const token = await getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(getFlowExecutionUrl(request.flowId), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        trigger_data: request.initialData || {},
        organizationId: request.organizationId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    return {
      success: result.success,
      executionId: result.execution_id,
      result: result.result,
      message: result.message
    };
  } catch (error) {
    console.error('Error executing flow:', error);
    throw new Error(`Failed to execute flow: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get flow execution history
 */
export async function getFlowExecutions(
  flowId: string,
  organizationId: string,
  limit: number = 20
): Promise<any[]> {
  try {
    const executionsQuery = query(
      collection(db, 'flowExecutions'),
      where('flowId', '==', flowId),
      where('organizationId', '==', organizationId),
      orderBy('startedAt', 'desc'),
      firestoreLimit(limit)
    );
    
    const querySnapshot = await getDocs(executionsQuery);
    
    const executions = querySnapshot.docs.map(doc => {
      const data = doc.data();
      
      // ExecutionLog timestamp konvertálása
      const executionLog = (data.executionLog || []).map((logEntry: any) => ({
        ...logEntry,
        timestamp: logEntry.timestamp?.toDate ? logEntry.timestamp.toDate() : 
                  logEntry.timestamp ? new Date(logEntry.timestamp) : new Date()
      }));
      
      return {
        id: doc.id,
        ...data,
        startedAt: data.startedAt?.toDate(),
        completedAt: data.completedAt?.toDate(),
        // Alapértelmezett értékek biztosítása
        executionLog,
        completedNodes: data.completedNodes || [],
        failedNodes: data.failedNodes || [],
        variables: data.variables || {},
        status: data.status || 'unknown'
      };
    });
    
    return executions;
  } catch (error) {
    console.error('Error getting flow executions:', error);
    throw new Error(`Failed to get flow executions: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Duplicate flow
 */
export async function duplicateFlow(
  flowId: string,
  organizationId: string,
  userId: string,
  newName?: string
): Promise<{ success: boolean; flowId: string; message: string }> {
  try {
    const originalFlow = await getFlow(flowId, organizationId);
    
    const duplicatedFlow = {
      ...originalFlow,
      name: newName || `${originalFlow.name} (másolat)`,
      description: `${originalFlow.description || ''} (duplikált)`,
      id: undefined, // Remove ID so a new one is generated
      createdAt: undefined,
      updatedAt: undefined,
      version: 1
    };
    
    return await saveFlow(duplicatedFlow, organizationId, userId);
  } catch (error) {
    console.error('Error duplicating flow:', error);
    throw new Error(`Failed to duplicate flow: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Validate flow structure
 */
export function validateFlow(nodes: FlowNode[], edges: FlowEdge[]): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Check for trigger node (csak ha vannak node-ok)
  if (nodes.length > 0) {
    const triggerNodes = nodes.filter(node => 
      node.type === 'trigger' || 
      node.type.endsWith('_trigger') ||
      ['webhook_trigger', 'schedule_trigger', 'form_trigger', 'email_trigger', 'file_trigger', 'database_trigger', 'manual_trigger'].includes(node.type)
    );
    if (triggerNodes.length === 0) {
      errors.push('Flow must have at least one trigger node');
    } else if (triggerNodes.length > 1) {
      warnings.push('Flow has multiple trigger nodes - only the first one will be used');
    }
  }
  
  // Check for orphaned nodes (csak ha vannak node-ok és edge-ek)
  if (nodes.length > 1) { // Csak ha több node van
    const connectedNodeIds = new Set();
    edges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });
    
    const orphanedNodes = nodes.filter(node => 
      !(node.type === 'trigger' || 
        node.type.endsWith('_trigger') ||
        ['webhook_trigger', 'schedule_trigger', 'form_trigger', 'email_trigger', 'file_trigger', 'database_trigger', 'manual_trigger'].includes(node.type)
      ) && !connectedNodeIds.has(node.id)
    );
    
    if (orphanedNodes.length > 0) {
      warnings.push(`${orphanedNodes.length} orphaned nodes found (not connected to flow)`);
    }
  }
  
  // Check for circular dependencies (csak ha vannak edge-ek)
  if (edges.length > 0) {
    const visited = new Set();
    const recursionStack = new Set();
    
    const hasCycle = (nodeId: string): boolean => {
      if (recursionStack.has(nodeId)) {
        return true;
      }
      if (visited.has(nodeId)) {
        return false;
      }
      
      visited.add(nodeId);
      recursionStack.add(nodeId);
      
      const outgoingEdges = edges.filter(edge => edge.source === nodeId);
      for (const edge of outgoingEdges) {
        if (hasCycle(edge.target)) {
          return true;
        }
      }
      
      recursionStack.delete(nodeId);
      return false;
    };
    
    for (const node of nodes) {
      if (hasCycle(node.id)) {
        errors.push('Flow contains circular dependencies');
        break;
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Export flow as JSON
 */
export function exportFlow(flow: FlowTemplate): string {
  const exportData = {
    ...flow,
    exportedAt: new Date().toISOString(),
    exportVersion: '1.0'
  };
  
  return JSON.stringify(exportData, null, 2);
}

/**
 * Import flow from JSON
 */
export function importFlow(jsonData: string): Partial<FlowTemplate> {
  try {
    const flowData = JSON.parse(jsonData);
    
    // Remove system fields that shouldn't be imported
    const {
      id,
      createdAt,
      updatedAt,
      createdBy,
      updatedBy,
      organizationId,
      exportedAt,
      exportVersion,
      ...importableData
    } = flowData;
    
    // Add import suffix to name
    if (importableData.name) {
      importableData.name = `${importableData.name} (importált)`;
    }
    
    return importableData;
  } catch (error) {
    throw new Error(`Invalid flow JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
